import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import PageContainer from '../common/PageContainer';
import ProductRecommendationsView from './ProductRecommendationsView';
import OverrideDialog from './OverrideDialog';
import useOverrides from './hooks/useOverrides';

/**
 * Main recommendations page with list/graph view toggle.
 */
export default function RecommendationsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams] = useSearchParams();
  const anchorParam = useMemo(() => {
    return (
      searchParams.get('anchor')
      || searchParams.get('anchor_product_id')
      || searchParams.get('product_id')
      || null
    );
  }, [searchParams]);
  const [compactMode, setCompactMode] = useState(false);

  // Override dialog state
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [selectedSourceProduct, setSelectedSourceProduct] = useState(null);

  // Override mutations
  const { createOverride, isPending } = useOverrides();

  // Ref to store refetch callback from child views
  const refetchCallbackRef = useRef(null);

  // Handle opening override dialog
  // Accepts optional refetch callback to trigger after save
  const handleOpenOverrideDialog = useCallback((recommendation, sourceProduct, refetchCallback) => {
    setSelectedRecommendation(recommendation);
    setSelectedSourceProduct(sourceProduct);
    refetchCallbackRef.current = refetchCallback || null;
    setOverrideDialogOpen(true);
  }, []);

  // Handle closing override dialog
  const handleCloseOverrideDialog = useCallback(() => {
    setOverrideDialogOpen(false);
    setSelectedRecommendation(null);
  }, []);

  // Handle saving override
  const handleSaveOverride = useCallback(async (overrideData) => {
    const {
      source_product_id,
      target_product_id,
      decision,
      relation_type,
      weight_adjustment,
      note,
    } = overrideData;

    await createOverride(source_product_id, target_product_id, decision, {
      relationType: relation_type,
      weightAdjust: weight_adjustment,
      note,
    });

    // Trigger refetch to update UI
    if (refetchCallbackRef.current) {
      refetchCallbackRef.current();
    }
  }, [createOverride]);

  return (
    // 🔴 G0b.5 (2026-10-13): nackter `Container` → `PageContainer width="wide"`.
    // Benannte sichtbare Aenderung `/recommendations`: 1440 → 1600
    // (`inhaltsbreite.md` §5.3:721, Plan §3.3).
    <PageContainer width="wide" sx={{ py: 3 }}>
      {/* Compact toggle — hidden on mobile */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={compactMode}
                onChange={(e) => setCompactMode(e.target.checked)}
                size="small"
              />
            }
            label="Compact"
          />
        </Box>
      )}

      <ProductRecommendationsView
        onOpenOverrideDialog={handleOpenOverrideDialog}
        compactMode={compactMode}
        initialProductId={anchorParam}
      />

      {/* Override Dialog */}
      <OverrideDialog
        open={overrideDialogOpen}
        onClose={handleCloseOverrideDialog}
        recommendation={selectedRecommendation}
        sourceProduct={selectedSourceProduct}
        onSave={handleSaveOverride}
        isLoading={isPending}
      />
    </PageContainer>
  );
}
